import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, User, X, Check, BookUser, ClipboardPaste } from 'lucide-react';
import { formatTelefoneBR, mascaraTelefoneBR, telefoneNoTexto } from '@fiodecobre/shared';
import { appApi, ErroApp, type ClienteFinalApp, type CorpoClienteFinal } from './api';
import { estiloCampo } from './campos';

// AppCel — CADASTRO DE CLIENTES DO PRESTADOR (migration 0260).
//
// Escopo fechado com o Ruan em planejamento (29/08/2026): os clientes DELE, nao os
// nossos — mesmo universo do `cliente_final` da Agenda e do "Meus Orçamentos". Esta
// tela e' o "listar, criar, editar, excluir"; o combo que a Agenda usa pra ESCOLHER
// um cliente mora em `AgendaApp.tsx`, e reusa o `ModalNovoClienteFinal` exportado
// daqui pro botão `+` de "cadastrar na hora".
//
// ⛔ **Nenhuma tela do ERP le esta tabela** — mesmo sigilo dos Orçamentos e da
// Agenda ([DA-565]/[DA-578]). Nao ha busca em `clientes`, nao ha ponteiro pra ficha
// nossa: e' cadastro solto, do jeito que ele pediu.
//
// ⚠️ **So nome e telefone no cadastro RÁPIDO** (`ModalNovoClienteFinal`) — endereço e
// observações entram na edição completa, aqui dentro. Decisão de produto: quem esta
// no meio de marcar um compromisso quer digitar o mínimo e seguir; quem quer guardar
// o endereço completo abre "Meus Clientes" com calma. Nenhuma das duas informações
// se perde — o quick-add so adia a segunda metade do cadastro.

const COBRE = '#b45309';
const CINZA = '#6b7280';
const BORDA = '#e7e5e4';
const DESTAQUE = '#fde68a';

/** O que o componente pede pro cabeçalho do aplicativo mostrar — mesmo formato do
 *  `CtxArea` de `Aplicativo.tsx`. */
interface Ctx {
  titulo: string;
  voltar: (() => void) | null;
}

/*
  ⚠️ **Aqui vivia uma mascara caseira, e ela so enxergava o numero DIGITADO.**
  O texto que este campo mostra e' o que vai pro banco, entao o que ela deixasse
  torto ninguem mais consertava: `separarDdiBR` no servidor recebe "(02) 16499-2326"
  e nao tem como adivinhar o numero de volta.

  Trocada pelo `mascaraTelefoneBR` do shared (30/08/2026), que tira DDI e prefixo
  de operadora ANTES de cortar em 11 — os dois caminhos por onde um numero chega
  COLADO neste campo: o autofill do iPhone e o copiar do WhatsApp.
*/

// ---------------------------------------------------------------------------
// TRAZER O CONTATO DO APARELHO — o botão ao lado do Telefone
// ---------------------------------------------------------------------------

/**
 * A ideia foi dele, em 30/08/2026: *"no campo de telefone, dá para colocar o botão
 * de compartilhar, ... o app vai pegar o telefone e trazer para o app"*.
 *
 * ⛔ **Pelo COMPARTILHAR nao da', e a razao e' a direcao**: o `navigator.share` e'
 * mao unica — a promessa dele resolve com `undefined`, sem dado de volta e **sem
 * dizer nem pra qual aplicativo foi**. O caminho contrario (o app se registrar como
 * DESTINO de compartilhamento, o *Share Target* do manifesto) existe no Chrome
 * Android desde a 76 e no Safari e' `version_added: false`, com o pedido aberto no
 * WebKit desde 2019 (medido no `mdn/browser-compat-data`, 30/08/2026).
 *
 * ✅ **O que a ideia dele pede existe por outra porta, e sem passar pelo WhatsApp**:
 * o proprio navegador abre a AGENDA do aparelho (`navigator.contacts`, Chrome
 * Android 80+) — que e' de onde o WhatsApp tira os contatos — e devolve nome e
 * telefone. Medido na base do AppCel no mesmo dia: **8 aparelhos Android em 6
 * contas** contra 4 iOS em 3.
 *
 * ⚠️ **No iPhone o botao NAO some — ele vira "Colar"** (area de transferencia, do
 * Safari 13.1 em diante). Botao que existe num aparelho e desaparece no outro faz
 * quem esta no iPhone concluir que o recurso nao existe; aqui cada aparelho recebe
 * o melhor caminho que ELE tem, com o rotulo dizendo qual e'.
 *
 * ⛔ **Nada disto se decide por user agent** — a pergunta e' sempre *"este aparelho
 * TEM a peca?"*, e no Android ainda ha o `getProperties()`: navegador que nao ofereca
 * `tel` cai no Colar em vez de abrir um seletor que volta sem telefone.
 */
interface ContatoDoAparelho {
  name?: string[];
  tel?: string[];
}

interface SeletorDeContatos {
  select: (props: string[], opcoes?: { multiple?: boolean }) => Promise<ContatoDoAparelho[]>;
  getProperties: () => Promise<string[]>;
}

function seletorDeContatos(): SeletorDeContatos | null {
  const n = navigator as Navigator & { contacts?: SeletorDeContatos };
  // `ContactsManager` no `window` e' o que separa o seletor de verdade de um
  // `navigator.contacts` de outra especie (a extensao antiga do Firefox OS).
  return n.contacts && 'ContactsManager' in window ? n.contacts : null;
}

function BotaoTrazerContato({ aoTrazer, aoAvisar }: {
  /** O que veio do aparelho. `nome` e' `null` quando o contato nao tem nome. */
  aoTrazer: (c: { nome: string | null; telefone: string | null }) => void;
  /** Falha que o usuário precisa ler — cancelar nao e' falha e nao avisa nada. */
  aoAvisar: (mensagem: string) => void;
}): React.ReactElement | null {
  const [modo, setModo] = useState<'agenda' | 'colar' | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    let vivo = true;
    const podeColar = typeof navigator.clipboard?.readText === 'function';
    const seletor = seletorDeContatos();
    if (!seletor) {
      setModo(podeColar ? 'colar' : null);
      return () => { vivo = false; };
    }
    seletor.getProperties()
      .then((props) => { if (vivo) setModo(props.includes('tel') ? 'agenda' : (podeColar ? 'colar' : null)); })
      .catch(() => { if (vivo) setModo(podeColar ? 'colar' : null); });
    return () => { vivo = false; };
  }, []);

  if (!modo) return null;

  async function daAgenda(): Promise<void> {
    const seletor = seletorDeContatos();
    if (!seletor) return;
    try {
      const escolhidos = await seletor.select(['name', 'tel'], { multiple: false });
      const c = escolhidos[0];
      if (!c) return; // fechou sem escolher — nao e' erro
      const telefone = (c.tel ?? []).find((t) => t.replace(/\D/g, '').length >= 10) ?? null;
      if (!telefone) {
        aoAvisar('Esse contato não tem telefone salvo no aparelho.');
        return;
      }
      aoTrazer({ nome: (c.name ?? [])[0]?.trim() || null, telefone });
    } catch {
      aoAvisar('Não consegui abrir a agenda deste aparelho. Dá pra colar o número no campo.');
    }
  }

  async function colar(): Promise<void> {
    try {
      // ⚠️ O que se copia raramente e' o numero limpo — o `telefoneNoTexto` ACHA o
      // telefone dentro da mensagem, em vez de jogar a frase inteira na máscara
      // (que só tira os não-dígitos e grudaria os outros números junto).
      const achado = telefoneNoTexto(await navigator.clipboard.readText());
      if (!achado) {
        aoAvisar('Não achei um telefone na área de transferência. Copie o número no WhatsApp e toque em Colar.');
        return;
      }
      aoTrazer({ nome: null, telefone: achado });
    } catch {
      aoAvisar('O aparelho não deixou ler a área de transferência. Dá pra colar segurando o dedo no campo.');
    }
  }

  return (
    <button
      type="button"
      disabled={ocupado}
      onClick={() => {
        setOcupado(true);
        void (modo === 'agenda' ? daAgenda() : colar()).finally(() => setOcupado(false));
      }}
      style={{
        flexShrink: 0, height: 46, marginTop: 6, padding: '0 14px', borderRadius: 10,
        border: 'none', backgroundColor: ocupado ? '#d6d3d1' : COBRE, color: '#fff',
        fontSize: 14, fontWeight: 800, display: 'inline-flex', alignItems: 'center',
        gap: 6, whiteSpace: 'nowrap',
      }}
    >
      {modo === 'agenda' ? <BookUser size={17} /> : <ClipboardPaste size={17} />}
      {modo === 'agenda' ? 'Agenda' : 'Colar'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// A TELA — lista, com edição/criação como segundo nível
// ---------------------------------------------------------------------------

export function ClientesApp({ aoContexto, aoVoltar }: {
  aoContexto: (c: Ctx) => void;
  /** Sai da área de clientes de volta pra grade da Agenda — um nível acima daqui. */
  aoVoltar: () => void;
}): React.ReactElement {
  const [lista, setLista] = useState<ClienteFinalApp[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  /** `'novo'` = formulário em branco; um objeto = editando aquele. */
  const [editando, setEditando] = useState<ClienteFinalApp | 'novo' | null>(null);

  async function carregar(): Promise<void> {
    setCarregando(true);
    try {
      setLista(await appApi.clientesFinais());
      setErro('');
    } catch (e) {
      setErro((e as ErroApp).message);
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { void carregar(); }, []);

  useEffect(() => {
    if (editando) {
      aoContexto({
        titulo: editando === 'novo' ? 'Novo cliente' : (editando.nome || 'Cliente'),
        voltar: () => setEditando(null),
      });
    } else {
      aoContexto({ titulo: 'Meus Clientes', voltar: aoVoltar });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editando]);

  function aoSalvar(): void {
    setEditando(null);
    void carregar();
  }

  if (editando) {
    return (
      <FormularioCliente
        cliente={editando === 'novo' ? null : editando}
        aoSalvar={aoSalvar}
        aoCancelar={() => setEditando(null)}
        aoExcluido={() => { setEditando(null); void carregar(); }}
      />
    );
  }

  return (
    <div style={{ padding: '12px 12px 24px' }}>
      <button
        type="button"
        onClick={() => setEditando('novo')}
        style={{
          width: '100%', height: 52, borderRadius: 12, backgroundColor: COBRE, color: '#fff',
          fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8,
        }}
      >
        <Plus size={20} /> Novo cliente
      </button>

      {erro && (
        <div style={{ margin: '10px 2px', padding: 12, borderRadius: 10, backgroundColor: '#fff8ef', border: `1px solid ${BORDA}`, fontSize: 14 }}>
          {erro}
        </div>
      )}

      {carregando ? (
        <div style={{ padding: 24, textAlign: 'center', color: CINZA, fontSize: 14 }}>Carregando…</div>
      ) : lista.length === 0 ? (
        <div style={{ padding: '24px 20px 0', textAlign: 'center', color: CINZA }}>
          <div style={{ fontSize: 13.5, lineHeight: 1.45 }}>
            Nenhum cliente cadastrado ainda. Comece com o botão acima, ou cadastre na
            hora ao marcar um compromisso.
          </div>
        </div>
      ) : (
        <div style={{
          marginTop: 12, backgroundColor: '#fff', border: `1px solid ${BORDA}`,
          borderRadius: 14, overflow: 'hidden',
        }}>
          {lista.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setEditando(c)}
              style={{
                width: '100%', textAlign: 'left', padding: '13px 14px',
                borderTop: `1px solid #f2f0ee`, display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <span style={{ color: COBRE, flexShrink: 0, display: 'flex' }}><User size={18} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.nome}
                </span>
                {(c.telefone || c.endereco) && (
                  <span style={{ display: 'block', fontSize: 12.5, color: CINZA, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[c.telefone ? formatTelefoneBR(c.telefone) : null, c.endereco].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// O FORMULÁRIO — criar/editar/excluir, segundo nível da tela acima
// ---------------------------------------------------------------------------

function FormularioCliente({ cliente, aoSalvar, aoCancelar, aoExcluido }: {
  cliente: ClienteFinalApp | null;
  aoSalvar: () => void;
  aoCancelar: () => void;
  aoExcluido: () => void;
}): React.ReactElement {
  const [nome, setNome] = useState(cliente?.nome ?? '');
  const [telefone, setTelefone] = useState(cliente?.telefone ? mascaraTelefoneBR(cliente.telefone) : '');
  const [endereco, setEndereco] = useState(cliente?.endereco ?? '');
  const [observacoes, setObservacoes] = useState(cliente?.observacoes ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const nomeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (!cliente) nomeRef.current?.focus(); }, [cliente]);

  async function salvar(): Promise<void> {
    if (!nome.trim()) { setErro('Informe o nome.'); return; }
    setSalvando(true);
    setErro('');
    const corpo: CorpoClienteFinal = {
      nome: nome.trim(),
      telefone: telefone.trim() || null,
      endereco: endereco.trim() || null,
      observacoes: observacoes.trim() || null,
    };
    try {
      if (cliente) await appApi.salvarClienteFinal(cliente.id, corpo);
      else await appApi.criarClienteFinal(corpo);
      aoSalvar();
    } catch (e) {
      setErro((e as ErroApp).message);
      setSalvando(false);
    }
  }

  async function excluir(): Promise<void> {
    if (!cliente) return;
    setSalvando(true);
    try {
      await appApi.excluirClienteFinal(cliente.id);
      aoExcluido();
    } catch (e) {
      setErro((e as ErroApp).message);
      setSalvando(false);
    }
  }

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', padding: '12px 12px 0' }}>
      <Bloco>
        <Rotulo>Nome</Rotulo>
        <input
          ref={nomeRef}
          className="no-upper"
          style={{ ...estiloCampo, marginTop: 6 }}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Nome do cliente"
          maxLength={200}
        />
      </Bloco>

      <Bloco>
        <Rotulo>Telefone</Rotulo>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <input
            className="no-upper"
            style={{ ...estiloCampo, marginTop: 6, flex: 1, minWidth: 0 }}
            inputMode="tel"
            value={telefone}
            onChange={(e) => setTelefone(mascaraTelefoneBR(e.target.value))}
            placeholder="(64) 99999-9999"
          />
          {/* ⚠️ O nome só é preenchido quando o campo está VAZIO — quem já digitou
              um nome não pode ver o do contato entrar por cima do dele. */}
          <BotaoTrazerContato
            aoTrazer={(c) => {
              if (c.telefone) setTelefone(mascaraTelefoneBR(c.telefone));
              if (c.nome && !nome.trim()) setNome(c.nome);
              setErro('');
            }}
            aoAvisar={setErro}
          />
        </div>
        {/* Botão apagado DIZ por que (regra da casa, § UI) — este avisa ANTES de
            salvar, e' o que liga o botão de WhatsApp no compromisso. */}
        <div style={{ fontSize: 12, color: CINZA, marginTop: 6 }}>
          Com o telefone, o compromisso ganha um botão de WhatsApp pronto.
        </div>
      </Bloco>

      <Bloco>
        <Rotulo>Endereço</Rotulo>
        <textarea
          className="no-upper"
          style={{ ...estiloCampo, marginTop: 6, height: 62, padding: '10px 14px', lineHeight: 1.4, resize: 'none' }}
          value={endereco}
          onChange={(e) => setEndereco(e.target.value)}
          placeholder="Rua, número, bairro…"
          maxLength={2000}
        />
      </Bloco>

      <Bloco>
        <Rotulo>Observações</Rotulo>
        <textarea
          className="no-upper"
          style={{ ...estiloCampo, marginTop: 6, height: 62, padding: '10px 14px', lineHeight: 1.4, resize: 'none' }}
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          placeholder="Ponto de referência, preferências…"
          maxLength={2000}
        />
      </Bloco>

      {erro && (
        <div style={{ margin: '4px 2px 10px', padding: 12, borderRadius: 10, backgroundColor: '#fff8ef', border: `1px solid ${BORDA}`, fontSize: 14 }}>
          {erro}
        </div>
      )}

      <button
        type="button"
        onClick={aoCancelar}
        style={{ width: '100%', height: 46, borderRadius: 11, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 15, fontWeight: 700 }}
      >
        Cancelar
      </button>

      {cliente && (
        confirmandoExclusao ? (
          <div style={{ marginTop: 16, padding: 13, borderRadius: 11, backgroundColor: '#fff8ef', border: `1px solid ${BORDA}` }}>
            <div style={{ fontSize: 14.5 }}>
              Excluir <b>{cliente.nome}</b>?
            </div>
            <div style={{ fontSize: 12.5, color: CINZA, marginTop: 3 }}>
              Não dá pra desfazer. Compromissos que apontam pra este cliente continuam
              na agenda, só perdem o vínculo.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
              <button
                type="button"
                onClick={() => setConfirmandoExclusao(false)}
                style={{ flex: 1, height: 44, borderRadius: 9, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 14.5, fontWeight: 700 }}
              >
                Não
              </button>
              <button
                type="button"
                onClick={() => void excluir()}
                disabled={salvando}
                style={{ flex: 1, height: 44, borderRadius: 9, backgroundColor: DESTAQUE, color: '#000', fontSize: 14.5, fontWeight: 800 }}
              >
                Excluir
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmandoExclusao(true)}
            style={{ width: '100%', height: 46, marginTop: 16, borderRadius: 11, border: `1px solid ${BORDA}`, backgroundColor: '#fff', color: '#9a3412', fontSize: 14.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}
          >
            <Trash2 size={16} /> Excluir cliente
          </button>
        )
      )}

      <div style={{ flex: 1 }} />
      <div style={{
        position: 'sticky', bottom: 0, marginTop: 12,
        marginLeft: -12, marginRight: -12, padding: '10px 12px 12px',
        backgroundColor: '#f7f7f7', borderTop: `1px solid ${BORDA}`,
      }}>
        <button
          type="button"
          onClick={() => void salvar()}
          disabled={salvando || !nome.trim()}
          style={{
            width: '100%', height: 54, borderRadius: 12,
            backgroundColor: salvando || !nome.trim() ? '#d6d3d1' : COBRE, color: '#fff',
            fontSize: 16.5, fontWeight: 800, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8,
          }}
        >
          <Check size={20} /> {salvando ? 'Salvando…' : 'Salvar'}
        </button>
        {!nome.trim() && (
          <div style={{ fontSize: 13, color: CINZA, textAlign: 'center', marginTop: 7 }}>
            Informe o nome para salvar.
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// O CADASTRO RÁPIDO — usado pelo botão "+" do combo de cliente na Agenda
// ---------------------------------------------------------------------------

/**
 * A receita de `CatalogoFornecedor.tsx` (`<ComboBox>` + `<ModalCadastroRapido>`),
 * adaptada pro AppCel: o `ModalCadastroRapido` do ERP fala com `lib/api.ts` (a
 * credencial da EQUIPE) e forca maiúsculas — nenhum dos dois serve aqui, onde a
 * credencial é a do CLIENTE (`appApi`, token separado) e o texto do prestador não
 * é uppercase (mesma razão do campo "Para quem é o serviço" da folha). Por isso
 * este modal é próprio, no estilo do AppCel, mas com a MESMA forma de uso: título,
 * `aoFechar`, `aoSalvo(novo)`.
 *
 * ⚠️ **Z-INDEX 70** (regra da casa) — abre de DENTRO da folha do compromisso, que é
 * tela cheia do app, não um modal aninhado; `z-[1100]` seria o andar de quem abre
 * de dentro de OUTRO modal, que não é o caso.
 */
export function ModalNovoClienteFinal({ aoFechar, aoSalvo }: {
  aoFechar: () => void;
  aoSalvo: (novo: ClienteFinalApp) => void;
}): React.ReactElement {
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const nomeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { nomeRef.current?.focus(); }, []);

  async function salvar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!nome.trim()) { setErro('Informe o nome.'); return; }
    setSalvando(true);
    setErro('');
    try {
      const novo = await appApi.criarClienteFinal({ nome: nome.trim(), telefone: telefone.trim() || null });
      aoSalvo(novo);
    } catch (e2) {
      setErro((e2 as ErroApp).message);
      setSalvando(false);
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={aoFechar}
    >
      <form
        onSubmit={(e) => void salvar(e)}
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 380, backgroundColor: '#fff', borderRadius: 16, overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 14px 10px' }}>
          <span style={{ flex: 1, fontSize: 16, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7 }}>
            <Plus size={16} style={{ color: COBRE }} /> Novo cliente
          </span>
          <button type="button" onClick={aoFechar} aria-label="Fechar" style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CINZA }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ display: 'block' }}>
            <Rotulo>Nome</Rotulo>
            <input
              ref={nomeRef}
              className="no-upper"
              style={{ ...estiloCampo, marginTop: 6 }}
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Nome do cliente"
              maxLength={200}
            />
          </label>
          <div>
            <Rotulo>Telefone (opcional)</Rotulo>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input
                className="no-upper"
                style={{ ...estiloCampo, marginTop: 6, flex: 1, minWidth: 0 }}
                inputMode="tel"
                value={telefone}
                onChange={(e) => setTelefone(mascaraTelefoneBR(e.target.value))}
                placeholder="(64) 99999-9999"
              />
              {/* ⚠️ Aqui o `<label>` virou `<div>`: envolvendo o botão, o toque nele
                  também focava o input (é o que um label faz), e no Android isso
                  subia o teclado por cima do seletor de contatos. */}
              <BotaoTrazerContato
                aoTrazer={(c) => {
                  if (c.telefone) setTelefone(mascaraTelefoneBR(c.telefone));
                  if (c.nome && !nome.trim()) setNome(c.nome);
                  setErro('');
                }}
                aoAvisar={setErro}
              />
            </div>
          </div>

          {erro && (
            <div style={{ padding: 10, borderRadius: 9, backgroundColor: '#fff8ef', border: `1px solid ${BORDA}`, fontSize: 13.5 }}>
              {erro}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={aoFechar}
              style={{ flex: 1, height: 46, borderRadius: 10, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 14.5, fontWeight: 700 }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvando || !nome.trim()}
              style={{
                flex: 1, height: 46, borderRadius: 10, border: 'none',
                backgroundColor: salvando || !nome.trim() ? '#d6d3d1' : COBRE, color: '#fff',
                fontSize: 14.5, fontWeight: 800,
              }}
            >
              {salvando ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// Rotulo/Bloco locais — versão mínima da mesma peça de `AgendaApp.tsx`. Não
// importada de lá de propósito: são primitivos de layout do próprio arquivo, não
// um design system compartilhado (mesmo padrão já usado nesta pasta).
function Bloco({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{
      backgroundColor: '#fff', border: `1px solid ${BORDA}`, borderRadius: 14,
      padding: 13, marginBottom: 12,
    }}>
      {children}
    </div>
  );
}

function Rotulo({ children }: { children: React.ReactNode }): React.ReactElement {
  return <span style={{ fontSize: 13, fontWeight: 700, color: CINZA }}>{children}</span>;
}
